import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../ui/button";
import { Card } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../ui/alert-dialog";
import { Plus, Pencil, Eye, Trash2, FileText, Loader2 } from "lucide-react";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { useToast } from "../../../../hooks/use-toast";

type Template = {
  id: string;
  name: string | null;
  subject_line: string | null;
  type: string | null;
  created_at: string | null;
};

const typeLabel = (t: string | null) => {
  switch (t) {
    case "event":
      return { label: "Event", className: "bg-purple-500/10 text-purple-600 border-purple-500/20" };
    case "automation":
      return { label: "Automation", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" };
    case "campaign":
      return { label: "Campaign", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" };
    default:
      return { label: "Manual", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" };
  }
};

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const Templates = () => {
  const navigate = useNavigate();
  const { merchant } = useMerchant();
  const { toast } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<Template | null>(null);

  const load = useCallback(async () => {
    if (!merchant) return;
    setLoading(true);
    const { data, error } = await epicengageSupabasev1
      .from("email_templates")
      .select("id, name, subject_line, type, created_at")
      .eq("merchant_id", merchant.id)
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Failed to load templates", description: error.message, variant: "destructive" });
    } else {
      setTemplates((data ?? []) as Template[]);
    }
    setLoading(false);
  }, [merchant, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Backwards-compat: ?new=1 redirects to the new editor page
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
      navigate("/clientpanel/enagev1/templates/new");
    }
  }, [searchParams, setSearchParams, navigate]);

  const handleDelete = async () => {
    if (!deleting) return;
    const { error } = await epicengageSupabasev1.from("email_templates").delete().eq("id", deleting.id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Template deleted" });
      setTemplates((prev) => prev.filter((t) => t.id !== deleting.id));
    }
    setDeleting(null);
  };

  return (
    <div>
      <div className="px-10 py-10">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Templates</h1>
            <p className="text-sm text-muted-foreground mt-1">Reusable email templates for your campaigns</p>
          </div>
          <Button onClick={() => navigate("/clientpanel/enagev1/templates/new")}>
            <Plus className="h-4 w-4" />
            Create Template
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-16 text-center">
            <FileText className="h-10 w-10 mx-auto text-muted-foreground/60 mb-3" />
            <p className="text-sm font-medium">No templates yet</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Create your first reusable email template</p>
            <Button onClick={() => navigate("/clientpanel/enagev1/templates/new")} variant="outline">
              <Plus className="h-4 w-4" />
              Create Template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => {
              const tag = typeLabel(t.type);
              return (
                <Card key={t.id} className="p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-base leading-tight line-clamp-2">{t.name || "Untitled"}</h3>
                    <Badge variant="outline" className={tag.className}>{tag.label}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
                    {t.subject_line || "No subject"}
                  </p>
                  <p className="text-xs text-muted-foreground">Created {formatDate(t.created_at)}</p>
                  <div className="flex items-center gap-2 pt-2 border-t border-border mt-auto">
                    <Button size="sm" variant="ghost" className="flex-1" onClick={() => navigate(`/clientpanel/enagev1/templates/${t.id}/edit`)}>
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="flex-1" onClick={() => navigate(`/clientpanel/enagev1/templates/${t.id}/preview`)}>
                      <Eye className="h-3.5 w-3.5" />
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleting(t)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleting?.name || "Untitled"}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Templates;
